import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Loader2, Trophy, Flame, Users, Calendar, ChevronUp, Link as LinkIcon, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { fetchSundaeGroupWeek } from '../../services/sundaeApi';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Pastor Mode — 7-day rolling group leaderboard view.
 *
 * Activated via the `?pastor=<comma-separated-user-ids>` URL param. The
 * pastor pre-builds the link by collecting user_ids from his small group
 * and pastes the full URL into the church bulletin / WhatsApp / email.
 * Anyone (including non-logged-in viewers) can open the link and see the
 * read-only leaderboard — no auth required for the endpoint itself.
 *
 * UI:
 *   - Header strip: Group · 7-day window · members count
 *   - 7-day column: per-day completers + top sundae score
 *   - Member rows: rank · name · days completed (7 dots) · best streak · total
 *   - Copy-link button to share the same URL
 */
const PastorModeView = ({ language = 'en', userIds = [], onCollapse }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!userIds || userIds.length === 0) {
      setError(t('games.pastor.emptyList'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchSundaeGroupWeek(userIds)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load group week');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIds.join(',')]);

  const onCopyLink = async () => {
    try {
      // Build the PUBLIC pastor URL so unauthenticated viewers can open
      // it directly. We always link to /pastor?ids=... regardless of how
      // the current page was reached.
      const idsParam = (userIds || []).join(',');
      const url = `${window.location.origin}/pastor?ids=${encodeURIComponent(idsParam)}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('games.pastor.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t('games.pastor.couldNotCopy'));
    }
  };

  return (
    <Card
      className="border-2 border-purple-300 dark:border-purple-700 shadow-lg"
      data-testid="pastor-mode-view"
    >
      <CardContent className="p-4 sm:p-5 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-700 dark:text-purple-300" />
              <h3 className="font-bold text-base sm:text-lg text-purple-900 dark:text-purple-100">
                {t('games.pastor.title')}
              </h3>
            </div>
            {data && (
              <p className="text-xs text-purple-800/80 dark:text-purple-200/80 italic mt-0.5">
                {data.start_date} → {data.end_date} ·{' '}
                {data.group_size} {t('games.pastor.members')} ·{' '}
                {data.total_completers} {t('games.pastor.active')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={onCopyLink}
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              data-testid="pastor-copy-link-btn"
            >
              {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <LinkIcon className="w-3.5 h-3.5 mr-1" />}
              {t('games.pastor.copyLink')}
            </Button>
            {onCollapse && (
              <Button
                onClick={onCollapse}
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0"
                data-testid="pastor-collapse-btn"
              >
                <ChevronUp className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-6 text-muted-foreground text-xs">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            {t('games.loading')}
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 dark:text-red-400 text-center py-3">
            {error}
          </p>
        )}

        {data && !loading && !error && (
          <>
            {/* 7-day column strip */}
            <div className="grid grid-cols-7 gap-1.5">
              {data.days.map((d) => (
                <div
                  key={d.date}
                  className={`rounded-md p-2 text-center border ${
                    d.completers > 0
                      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700'
                      : 'bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800'
                  }`}
                  data-testid={`pastor-day-${d.date}`}
                >
                  <div className="text-[9px] font-mono text-muted-foreground">
                    {d.date.slice(5)}
                  </div>
                  <div className="text-base font-bold tabular-nums mt-0.5">
                    {d.completers}
                    <span className="text-[9px] opacity-50">/{data.group_size}</span>
                  </div>
                  {d.top_score > 0 && (
                    <div className="flex items-center justify-center gap-0.5 text-[9px] text-yellow-700 dark:text-yellow-400 font-semibold tabular-nums mt-0.5">
                      <Trophy className="w-2.5 h-2.5" />
                      {d.top_score}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Member rows */}
            <div className="space-y-1 pt-2 border-t border-purple-200/40 dark:border-purple-800/40">
              {data.members.map((m, i) => (
                <div
                  key={m.user_id}
                  className={`flex items-center gap-3 px-2 py-2 rounded-md ${
                    i === 0 ? 'bg-yellow-50 dark:bg-yellow-950/20' : ''
                  }`}
                  data-testid={`pastor-member-${i + 1}`}
                >
                  <span className="text-xs font-mono w-5 text-muted-foreground tabular-nums">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {m.name || `${m.user_id.slice(0, 8)}…`}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {/* 7 dots — green when this member completed that
                          specific date; the endpoint returns
                          `completed_dates` so each dot is precise. */}
                      <div className="flex items-center gap-0.5">
                        {data.days.map((d) => {
                          const completed = (m.completed_dates || []).includes(d.date);
                          return (
                            <span
                              key={d.date}
                              className={`w-1.5 h-1.5 rounded-full ${
                                completed
                                  ? 'bg-emerald-500'
                                  : 'bg-zinc-300 dark:bg-zinc-700'
                              }`}
                              title={d.date}
                            />
                          );
                        })}
                      </div>
                      {m.best_streak_in_window > 1 && (
                        <Badge
                          variant="outline"
                          className="h-4 text-[9px] border-orange-300 text-orange-700 dark:text-orange-400"
                        >
                          <Flame className="w-2.5 h-2.5 mr-0.5" />
                          {m.best_streak_in_window}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className="font-bold tabular-nums text-sm"
                      data-testid={`pastor-member-score-${i + 1}`}
                    >
                      +{m.total_score}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {m.days_completed}/7 {t('games.pastor.days')}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-[10px] text-muted-foreground italic pt-2 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {t('games.pastor.footer')}
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PastorModeView;
